import { offeringsContract } from '@mntn/api-contracts/seller/v1/offerings'
import { useQuery } from '@tanstack/react-query'
import type { ClientInferResponseBody } from '@ts-rest/core'
import { useFlightDeckTsRestClient } from '@/flight-deck-api/ts-rest'

type Offering = ClientInferResponseBody<typeof offeringsContract.list, 200>['data'][number]

type UseGetOfferingResult =
  | {
      data: undefined
      error: Error | null
      isLoading: true
    }
  | {
      data: Offering
      error: null
      isLoading: false
    }

export const useGetOffering = ({
  offeringId,
  enabled = true,
  includePurchasedManagedSales = false,
}: {
  offeringId: number
  enabled?: boolean
  includePurchasedManagedSales?: boolean
}): UseGetOfferingResult => {
  const offeringsClient = useFlightDeckTsRestClient(offeringsContract)

  const query = useQuery({
    queryKey: ['getOffering', offeringId, includePurchasedManagedSales],

    queryFn: async () => {
      const response = await offeringsClient.list({
        query: {
          'offering-id': [offeringId],
          limit: 1,
          offset: 0,
          'include-purchased-managed-sales': includePurchasedManagedSales,
        },
      })

      if (response.status !== 200) {
        throw new Error('Failed to fetch latest offering')
      }

      const [offering] = response.body.data
      if (!offering) {
        throw new Error('Latest offering not found')
      }

      return offering
    },
    enabled,
  })

  if (query.data === undefined) {
    return {
      data: undefined,
      error: query.error,
      isLoading: true,
    }
  }

  // Retain the displayed offering if a background refresh fails; purchases still validate its version.
  return {
    data: query.data,
    error: null,
    isLoading: false,
  }
}
